import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {environment} from '../../../environments/environment';
import {ExpertPublicProfileDTO} from '../../dtos/expert';
import {BaseService, DataBaseServiceResponse} from '../../services/base-service/base.service';
import {IExpertPublicProfile} from './expert-public-profile.interface';
import {firstValueFrom} from 'rxjs';
import {IMedia} from '../../interfaces/cloudinary-transform';
import {cloneDeep} from 'lodash';
import {StorageService} from 'src/app/services/storage/storage.service';


export interface IRExpertPublicProfile {
  profile: IExpertPublicProfile;
  profileEnabled: boolean;
  galleryPictures: IMedia[];
}

export namespace ExpertPublicProfileAction {
  export class GetStatus {
    static readonly type = '[EXPERT_PUBLIC_PROFILE] Get Status Expert Public Profile Action';
    constructor(public clinicId: string) { }
  }

  export class Enable {
    static readonly type = '[EXPERT_PUBLIC_PROFILE] Enable Expert Public Profile Action';
    constructor(public clinicId: string) { }
  }

  export class Patch {
    static readonly type = '[EXPERT_PUBLIC_PROFILE] Patch Expert Public Profile Action';
    constructor(public clinicId: string, public publicProfile: ExpertPublicProfileDTO) { }
  }
  export class GetProfile {
    static readonly type = '[EXPERT_PUBLIC_PROFILE] Get Expert Public Profile Action';
    constructor(public clinicId: string) { }
  }

  export class GetGallery {
    static readonly type = '[EXPERT_PUBLIC_PROFILE] Get Gallery Expert Public Profile Action';
    constructor(public clinicId: string, public expertUserId: string) { }
  }

  export class DeleteGalleryById {
    static readonly type = '[EXPERT_PUBLIC_PROFILE] Delete Gallery Expert Public Profile Action';
    constructor(public clinicId: string, public mediaId: string) { }
  }

  export class AddGallery {
    static readonly type = '[EXPERT_PUBLIC_PROFILE] Add Picture to Gallery Expert Public Profile Action';
    constructor(public media: IMedia) { }
  }
}


@State<IRExpertPublicProfile>({
  name: 'ExpertPublicProfileState',
  defaults: {
    profile: null,
    profileEnabled: false,
    galleryPictures: [],
  }
})

@Injectable()
export class ExpertPublicProfileState {
  constructor(
    private baseService: BaseService<any>,
    private storageService: StorageService
  ) { }

  url = environment.SERVER_URL;

  @Selector()
  static isPublicProfileEnabled(state: IRExpertPublicProfile): boolean {
    return state.profileEnabled;
  }

  @Selector()
  static getExpertPublicProfile(state: IRExpertPublicProfile): IExpertPublicProfile {
    return state.profile;
  }

  @Selector()
  static getExpertProfileGalleryPictures(state: IRExpertPublicProfile): IMedia[] {
    return state.galleryPictures;
  }

  @Action(ExpertPublicProfileAction.GetStatus)
  async getStatusExpertPublicProfileAction(ctx: StateContext<IRExpertPublicProfile>, action: ExpertPublicProfileAction.GetStatus): Promise<void> {
    const response: DataBaseServiceResponse<{enabled: boolean}> = await firstValueFrom(
      this.baseService.get(`${this.url}/experts/${action.clinicId}/public-profile/enabled`)
    );

    if (response.error) throw new Error(response.message);

    this.storageService.setLocalItem('isPublicProfileEnabled', response.entity.enabled);
    ctx.patchState({
      profileEnabled: response.entity.enabled,
    });
  }

  @Action(ExpertPublicProfileAction.Enable)
  async enableExpertPublicProfileAction(ctx: StateContext<IRExpertPublicProfile>, action: ExpertPublicProfileAction.Enable): Promise<void> {
    const response: DataBaseServiceResponse<IExpertPublicProfile> = await firstValueFrom(
      this.baseService.post(`${this.url}/experts/${action.clinicId}/public-profile/enable`, {})
    );
    if (response.error) throw new Error(response.message);

    this.storageService.setLocalItem('isPublicProfileEnabled', true);
    ctx.patchState({
      profile: response.entity,
      profileEnabled: true
    });
  }

  @Action(ExpertPublicProfileAction.Patch)
  async patchExpertPublicProfileAction(ctx: StateContext<IRExpertPublicProfile>, action: ExpertPublicProfileAction.Patch): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.patch(`${this.url}/experts/${action.clinicId}/public-profile`, action.publicProfile)
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(ExpertPublicProfileAction.GetProfile)
  async getExpertPublicProfileAction(ctx: StateContext<IRExpertPublicProfile>, action: ExpertPublicProfileAction.GetProfile): Promise<void> {
    const response: DataBaseServiceResponse<IExpertPublicProfile> = await firstValueFrom(
      this.baseService.get(`${this.url}/experts/${action.clinicId}/public-profile`)
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      profile: response.entity,
    });
  }

  @Action(ExpertPublicProfileAction.GetGallery)
  async getGalleryPicturesExpertPublicProfileAction(ctx: StateContext<IRExpertPublicProfile>, action: ExpertPublicProfileAction.GetGallery): Promise<void> {
    const response: DataBaseServiceResponse<IMedia[]> = await firstValueFrom(
      this.baseService.get(`${this.url}/experts/${action.clinicId}/public-profile/${action.expertUserId}/gallery`)
    );
    if (response.error) throw new Error(response.message);

    ctx.patchState({
      galleryPictures: response.entity
    });
  }

  @Action(ExpertPublicProfileAction.DeleteGalleryById)
  async deleteGalleryPicturesExpertPublicProfileAction(
    ctx: StateContext<IRExpertPublicProfile>,
    action: ExpertPublicProfileAction.DeleteGalleryById
  ): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.delete(`${this.url}/experts/${action.clinicId}/public-profile/gallery/${action.mediaId}`)
    );

    if (response.error) throw new Error(response.message);

    const gallery: IMedia[] = cloneDeep(ctx.getState().galleryPictures);
    const pictureIndex = gallery.findIndex(media => media._id === action.mediaId);
    if (pictureIndex !== -1) {
      gallery.splice(pictureIndex, 1);
      ctx.patchState({
        galleryPictures: gallery,
      });
    }
  }

  @Action(ExpertPublicProfileAction.AddGallery)
  async addPictureToGalleryAction(ctx: StateContext<IRExpertPublicProfile>, action: ExpertPublicProfileAction.AddGallery): Promise<void> {
    const gallery: IMedia[] = cloneDeep(ctx.getState().galleryPictures);

    ctx.patchState({
      galleryPictures: [...gallery, action.media],
    });
  }
}
